import { useEffect, useState } from 'react'

/**
 * The two widths the layout actually changes at.
 *
 * Lifted from the prototype, which measures rather than guesses. They are not
 * device names — nothing here asks whether you are on a phone — they are the
 * points at which a specific piece of the room stops fitting:
 *
 *   - `narrow`:  the composer's hint goes. What the room header drops is the
 *                 header's own question, answered by a container query on it
 *                 (`.sp-room-header` in base.css), because the sidebar beside
 *                 it is dragged by the reader.
 *   - `compact`: the sidebar no longer fits beside the room. It becomes a
 *                 phone's Home and DMs tabs over a bar along the bottom, and
 *                 a room, the roster and activity each take the screen.
 *
 * Order matters: compact implies narrow.
 */
export const COMPACT_WIDTH = 900
export const NARROW_WIDTH = 1060

export interface Viewport {
  width: number
  compact: boolean
  narrow: boolean
}

function measure(): Viewport {
  const width = typeof window === 'undefined' ? 1440 : window.innerWidth
  const compact = width < COMPACT_WIDTH
  return {
    width,
    compact,
    narrow: width < NARROW_WIDTH,
  }
}

export function useViewport(): Viewport {
  const [viewport, setViewport] = useState<Viewport>(measure)

  useEffect(() => {
    function onResize() {
      setViewport((previous) => {
        const next = measure()
        // Re-rendering the whole workspace on every pixel of a drag is waste;
        // only a crossed boundary changes anything anyone can see.
        const same = next.compact === previous.compact && next.narrow === previous.narrow
        return same ? previous : next
      })
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  return viewport
}

const HOVER_QUERY = '(hover: hover)'

/**
 * Whether the pointer can hover: a mouse or a trackpad, not a finger. It decides
 * how a message offers its actions (a hover bar where it can, a long press where
 * it cannot) and which link Open in Claude app uses (`openLink`): a `claude://`
 * link opens a session on the Mac the agent runs on, which a phone cannot reach,
 * so without hover only a Remote Control session's claude.ai link is offered.
 * Width cannot answer this: a narrow desktop window has a mouse, and a tablet is
 * wide with none.
 *
 * True where `matchMedia` is missing, so such a browser keeps the hover bar.
 */
export function useCanHover(): boolean {
  const [canHover, setCanHover] = useState(() => window.matchMedia?.(HOVER_QUERY).matches ?? true)

  useEffect(() => {
    const query = window.matchMedia?.(HOVER_QUERY)
    if (!query) return
    const onChange = () => setCanHover(query.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  return canHover
}
